// Cards de configuracao da curadoria: categorias, ritmo, filtros e destinos.
// Todos controlados pelo rascunho do painel (AutoPromotionPanel).

import { Gauge, Filter, Send, Tags } from "lucide-react";
import {
  INTERVAL_OPTIONS,
  MAX_ITEMS_PER_CYCLE,
  MIN_ITEMS_PER_CYCLE,
  SHOPEE_CATEGORIES,
  type AutoPromotionConfig,
  type AutoPromotionFilters,
  type Pace,
} from "@lucralink/shared";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { PlatformBadge, selectClassName } from "@/components/forwarding/controls";
import type { ForwardingChannel } from "@/lib/hooks/useForwarding";
import { formatOptionalNumber, intervalLabel, parseOptionalNumber } from "@/lib/auto-promotions";
import { cn } from "@/lib/utils";

type Update = (patch: Partial<AutoPromotionConfig>) => void;

function SectionTitle({ icon: Icon, title, hint }: { icon: typeof Gauge; title: string; hint: string }): JSX.Element {
  return (
    <div className="mb-4 flex items-start gap-3">
      <Icon size={18} className="mt-0.5 shrink-0 text-primary" aria-hidden="true" />
      <div>
        <h2 className="text-sm font-semibold text-foreground">{title}</h2>
        <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>
      </div>
    </div>
  );
}

export function CategoriesCard({ config, onChange }: { config: AutoPromotionConfig; onChange: Update }): JSX.Element {
  const selected = new Set(config.categoryIds);
  const toggle = (id: string): void => {
    onChange({
      categoryIds: selected.has(id) ? config.categoryIds.filter((c) => c !== id) : [...config.categoryIds, id],
    });
  };
  return (
    <Card>
      <CardContent className="p-5">
        <SectionTitle
          icon={Tags}
          title={`Categorias (${selected.size} de ${SHOPEE_CATEGORIES.length})`}
          hint="A cada envio entra uma categoria diferente, em rodízio. Mais categorias sustentam um ritmo maior sem repetir produto."
        />
        <div className="flex flex-wrap gap-2" role="group" aria-label="Categorias da loja">
          {SHOPEE_CATEGORIES.map((category) => {
            const active = selected.has(category.id);
            return (
              <button
                key={category.id}
                type="button"
                aria-pressed={active}
                onClick={() => toggle(category.id)}
                className={cn(
                  "rounded-full border px-3 py-1 text-xs font-medium transition-colors duration-[120ms]",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  active
                    ? "border-primary/50 bg-primary/15 text-foreground"
                    : "border-[hsl(var(--border))] text-muted-foreground hover:border-[hsl(var(--border-strong))] hover:text-foreground",
                )}
              >
                {category.name}
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

interface PaceCardProps {
  readonly config: AutoPromotionConfig;
  readonly pace: Pace;
  readonly dailyQuota: number | null;
  readonly windowEnabled: boolean;
  readonly onChange: Update;
}

export function PaceCard({ config, pace, dailyQuota, windowEnabled, onChange }: PaceCardProps): JSX.Element {
  return (
    <Card>
      <CardContent className="p-5">
        <SectionTitle icon={Gauge} title="Ritmo" hint="Com que frequência e quantos produtos entram em cada envio." />
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="space-y-1.5 text-xs font-medium text-muted-foreground">
            Frequência
            <select
              className={selectClassName}
              value={config.intervalMinutes}
              onChange={(e) => onChange({ intervalMinutes: Number(e.target.value) })}
            >
              {INTERVAL_OPTIONS.map((minutes) => (
                <option key={minutes} value={minutes}>
                  {intervalLabel(minutes)}
                </option>
              ))}
            </select>
          </label>
          <label className="space-y-1.5 text-xs font-medium text-muted-foreground">
            Produtos por envio
            <Input
              type="number"
              min={MIN_ITEMS_PER_CYCLE}
              max={MAX_ITEMS_PER_CYCLE}
              value={config.itemsPerCycle}
              onChange={(e) => {
                const n = Math.round(Number(e.target.value));
                onChange({ itemsPerCycle: Math.min(MAX_ITEMS_PER_CYCLE, Math.max(MIN_ITEMS_PER_CYCLE, n || 1)) });
              }}
            />
          </label>
        </div>
        <div
          className={cn(
            "mt-4 rounded-lg border px-4 py-3 text-sm",
            pace.isSustainable ? "border-primary/30 bg-primary/5" : "border-[hsl(var(--gold)/0.4)] bg-[hsl(var(--gold)/0.08)]",
          )}
          aria-live="polite"
        >
          <p className="font-semibold text-foreground">
            {pace.productsPerDay} {pace.productsPerDay === 1 ? "produto" : "produtos"} por dia
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Suas categorias sustentam cerca de {pace.sustainablePerDay} por dia.{" "}
            {pace.isSustainable
              ? "Ritmo sustentável."
              : `Para esse ritmo, escolha pelo menos ${pace.minCategories} categorias.`}
          </p>
          {pace.cappedByQuota && dailyQuota !== null && (
            <p className="mt-1 text-xs text-[hsl(var(--gold))]">
              O plano Free envia até {dailyQuota} promoções por dia. No PRO não há limite.
            </p>
          )}
          {windowEnabled && (
            <p className="mt-1 text-xs text-muted-foreground">
              O cálculo considera a janela de envio do encaminhamento: fora dela nada sai.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

const FILTER_FIELDS: ReadonlyArray<{ key: keyof AutoPromotionFilters; label: string; suffix: string; cents?: boolean }> = [
  { key: "minDiscountPercent", label: "Desconto mínimo", suffix: "%" },
  { key: "minCommissionPercent", label: "Comissão mínima", suffix: "%" },
  { key: "minSales", label: "Vendas mínimas", suffix: "vendas" },
  { key: "minRating", label: "Nota mínima", suffix: "de 5" },
  { key: "maxPriceCents", label: "Preço máximo", suffix: "R$", cents: true },
];

export function FiltersCard({ config, onChange }: { config: AutoPromotionConfig; onChange: Update }): JSX.Element {
  const setFilter = (key: keyof AutoPromotionFilters, raw: string, cents: boolean): void => {
    const parsed = parseOptionalNumber(raw);
    const value = parsed === null || Number.isNaN(parsed) ? null : cents ? Math.round(parsed * 100) : parsed;
    onChange({ filters: { ...config.filters, [key]: value } });
  };
  return (
    <Card>
      <CardContent className="p-5">
        <SectionTitle icon={Filter} title="Filtros" hint="Só entram produtos que passam em todos. Deixe em branco para não filtrar." />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {FILTER_FIELDS.map((field) => {
            const value = config.filters[field.key];
            const shown = field.cents && value !== null ? value / 100 : value;
            return (
              <label key={field.key} className="space-y-1.5 text-xs font-medium text-muted-foreground">
                {field.label} ({field.suffix})
                <Input
                  inputMode="decimal"
                  defaultValue={formatOptionalNumber(shown)}
                  onBlur={(e) => setFilter(field.key, e.target.value, field.cents === true)}
                  placeholder="Sem filtro"
                />
              </label>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

interface DestinationsCardProps {
  readonly config: AutoPromotionConfig;
  readonly channels: readonly ForwardingChannel[];
  readonly maxDestinations: number;
  readonly onChange: Update;
}

export function DestinationsCard({ config, channels, maxDestinations, onChange }: DestinationsCardProps): JSX.Element {
  const available = channels.filter((c) => c.role === "destino");
  const chosen = new Set([...config.destinations.telegram, ...config.destinations.whatsapp]);
  const toggle = (channel: ForwardingChannel): void => {
    const list = config.destinations[channel.platform];
    const next = list.includes(channel.ref) ? list.filter((r) => r !== channel.ref) : [...list, channel.ref];
    onChange({ destinations: { ...config.destinations, [channel.platform]: next } });
  };
  return (
    <Card>
      <CardContent className="p-5">
        <SectionTitle
          icon={Send}
          title="Destinos das promoções"
          hint={`Escolha onde as promoções automáticas chegam. É separado do encaminhamento. Seu plano permite ${maxDestinations} ${maxDestinations === 1 ? "destino" : "destinos"}.`}
        />
        {available.length === 0 ? (
          <p className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
            Nenhum grupo ou canal de destino ainda. Marque um canal como destino em Grupos e canais.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {available.map((channel) => {
              const checked = chosen.has(channel.ref);
              const blocked = !checked && chosen.size >= maxDestinations;
              return (
                <li key={channel.id}>
                  <label className={cn("flex items-center gap-3 py-2.5 text-sm", blocked && "opacity-50")}>
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-[hsl(var(--primary))]"
                      checked={checked}
                      disabled={blocked}
                      onChange={() => toggle(channel)}
                    />
                    <span className="min-w-0 flex-1 truncate text-foreground">{channel.title ?? channel.ref}</span>
                    <PlatformBadge platform={channel.platform} />
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
